/**
 * Build identity of the running bundle: which environment it is, which release
 * (if any) it was cut from, and which commit it contains.
 *
 * Every input is baked in at build time by `vite build` — see the `VITE_APP_*`
 * block in `.github/workflows/deploy-frontend.yml` — so the badge describes the
 * deploy actually serving the page and nothing is fetched at runtime.
 *
 * The decisions live here rather than in `components/layout/VersionBadge.tsx`
 * so they are covered by the `src/lib` coverage gate; the components only
 * render what this returns. There are two surfaces: a chip in the sidebar
 * header, and a tooltip on the sidebar title. Which one a build gets is decided
 * here too, by `showChip`.
 */

/** Canonical repo every badge links into. */
export const REPO_URL = "https://github.com/aurora-tech-lovable/aurora-tms";

/** The branch a dev build links to. `main` is the repo's default branch. */
const DEFAULT_BRANCH = "main";

/** The four deploy targets, matching deploy-frontend.yml's `environment` input. */
export type AppEnv = "preview" | "dev" | "staging" | "prod";

const APP_ENVS: readonly string[] = ["preview", "dev", "staging", "prod"];

/**
 * Deploy targets that show their build identity as a chip in the sidebar
 * header. Staging and prod get the title tooltip instead.
 *
 * A chip is worth its space on the builds that move underfoot: a preview
 * redeploys on every push to the branch and dev on every merge to main, so
 * "which build am I looking at" is a live question there. A release is a
 * deliberate act you already know the name of, and the tooltip carries the same
 * string for anyone who needs to check.
 */
const CHIP_ENVS: readonly string[] = ["preview", "dev"];

/** The app's name, as the title tooltip spells it out. */
const APP_NAME = "ATMS: Aurora Transportation Management System";

/** 7 is git's own short-sha length. */
const SHORT_COMMIT_LENGTH = 7;

/**
 * A commit as `git rev-parse HEAD` emits it. Validated rather than trusted: an
 * unset Vite variable can arrive as a stringy placeholder, and slicing that to
 * 7 characters would put "undefin" in the tooltip. A build with no usable
 * commit shows none.
 */
const COMMIT_RE = /^[0-9a-f]{7,40}$/i;

/**
 * A release tag: `1.0.1` or `1.0.1-rc.0`. Guards against the version input
 * carrying something that is not a tag at all — promote-staging.yml falls back
 * to `github.ref_name`, which is a branch name if that workflow is ever started
 * from a branch, and "vmain" is worse than no version.
 */
const VERSION_RE = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.]+)?$/;

/**
 * A pull request number, as `github.event.pull_request.number` emits it.
 */
const PR_NUMBER_RE = /^\d+$/;

/**
 * A release line: the `X.Y` of a `release/X.Y` branch. Same shape next-rc-tag.sh
 * requires of the branch name. Guards against a branch that is not a release
 * line (or no branch at all) reaching the label as "vmain".
 */
const RELEASE_LINE_RE = /^\d+\.\d+$/;

export interface AppVersionInputs {
  /** Deploy target. Anything else — including a build with no CI at all — shows no badge. */
  env?: string;
  /** Release tag this build was cut from, e.g. "1.0.1-rc.0" or "1.0.1". */
  version?: string;
  /** Commit the bundle was built from. */
  commit?: string;
  /** Pull request number. Preview builds only. */
  prNumber?: string;
  /** Newest `release/X.Y` line, e.g. "1.0". Qualifies a preview's PR number. */
  releaseLine?: string;
}

export interface AppVersion {
  /** What the badge reads: "v1.0+pr-14", "dev", "v1.0.1-rc.0", "v1.0.1". */
  label: string;
  /** Where clicking it goes: the PR, the branch, the tag or the release. */
  href: string;
  /** Short commit hash, or null when the build carried none. */
  commit: string | null;
  /** What the link points at, spelled out for the chip's tooltip. */
  target: string;
  /**
   * Whether this build shows a chip. False means the title tooltip is the only
   * place its identity appears.
   */
  showChip: boolean;
}

function trimmed(value?: string): string {
  return typeof value === "string" ? value.trim() : "";
}

function shortCommit(commit: string): string | null {
  return COMMIT_RE.test(commit) ? commit.slice(0, SHORT_COMMIT_LENGTH) : null;
}

/**
 * Resolve the badge for one build, or null when the build has no identity to
 * show.
 *
 * Null is the deliberate answer for anything that is not one of the four deploy
 * targets — notably Lovable's production build, which runs no CI, reads the
 * committed `.env` and therefore carries no `VITE_APP_ENV`. A build with no
 * deploy target shows nothing rather than a badge describing the wrong deploy.
 */
export function resolveAppVersion(inputs: AppVersionInputs): AppVersion | null {
  const env = trimmed(inputs.env);
  if (!APP_ENVS.includes(env)) return null;

  const commit = shortCommit(trimmed(inputs.commit));
  const version = trimmed(inputs.version);
  const release = VERSION_RE.test(version) ? version : "";
  const line = trimmed(inputs.releaseLine);
  const releaseLine = RELEASE_LINE_RE.test(line) ? line : "";
  const showChip = CHIP_ENVS.includes(env);

  if (env === "preview") {
    const prNumber = trimmed(inputs.prNumber);
    // A bare PR number says nothing about what is in the build, so it is
    // qualified with the release line it sits on: "v1.0+pr-54". The "+" is
    // semver build metadata, which is what this is: release 1.0 plus PR 54.
    if (PR_NUMBER_RE.test(prNumber)) {
      return {
        label: releaseLine ? `v${releaseLine}+pr-${prNumber}` : `pr-${prNumber}`,
        href: `${REPO_URL}/pull/${prNumber}`,
        commit,
        target: `PR #${prNumber}`,
        showChip,
      };
    }
    // No PR number: all we know is that this is not one of the three long-lived
    // environments.
    return { label: "preview", href: REPO_URL, commit, target: "Repository", showChip };
  }

  if (env === "dev") {
    return {
      // "dev" alone does not say which build you are looking at, and dev moves
      // on every merge to main. The commit is the only thing that distinguishes
      // one dev build from the next, so it goes in the label. A build that
      // carries no commit falls back to the bare word.
      label: commit ? `dev-${commit}` : "dev",
      href: `${REPO_URL}/tree/${DEFAULT_BRANCH}`,
      commit,
      target: `${DEFAULT_BRANCH} branch`,
      showChip,
    };
  }

  // staging and prod are both cut from a tag; only what we call the link differs.
  if (release) {
    return {
      label: `v${release}`,
      href: `${REPO_URL}/releases/tag/${release}`,
      commit,
      target: env === "staging" ? `Tag ${release}` : `Release ${release}`,
      showChip,
    };
  }

  return { label: env, href: REPO_URL, commit, target: "Repository", showChip };
}

/**
 * The sidebar title's tooltip, one entry per line: the app's name, then the
 * build identity under it.
 *
 * Two lines and nothing else. The commit and the link belong to the chip
 * (see VersionBadge.tsx), which is what a build you might act on shows; on
 * staging and prod there is nothing to act on, so there is nothing to link.
 */
export function versionTooltip(label: string): [string, string] {
  return [APP_NAME, label];
}
